// Face check-in / check-out window: GET /attendance/face/challenge (a random
// head-turn order such as "right-left", one nonce per attempt), oval guide,
// 3-2-1 countdown, then a 2 s burst of 6 frames with the challenge's prompts
// ("Look right" → "Look left" …) and a progress bar (no shutter sound), then
// POST /attendance/face/check-in with the frames + challenge + nonce.
//
// A server without /challenge (404) falls back to the fixed
// left-centre-right prompts and sends no nonce.
//
// Server answers are shown verbatim next to our translation of the code. After
// two rejections the fallback (the regular punch, exactly as today) becomes the
// prominent button. A 409 means the enrollment must be renewed.
import React, { useCallback, useEffect, useRef, useState } from "react";
import { ScanFace, RotateCcw, KeyRound, UserRound } from "lucide-react";
import RemotePunchDialog, { dialogColors } from "../RemotePunch/RemotePunchDialog";
import FaceCameraView from "./FaceCameraView";
import { useFaceCapture, CAMERA_STATUS } from "../../pages/(User)attendance_page/hooks/useFaceCapture";
import { faceApi } from "./faceApi";
import { faceText, describeFaceError, distanceHint } from "./faceText";

export const FRAME_COUNT = 6;
export const BURST_MS = 2000;
export const DEFAULT_STEPS = ["left", "center", "right"];
const COUNTDOWN_FROM = 3;
const FALLBACK_AFTER = 2;

const PHASE = { CAMERA: "camera", COUNTDOWN: "countdown", CAPTURING: "capturing", VERIFYING: "verifying", ERROR: "error" };

const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

export default function FaceCheckInModal({
  open,
  type = "in",          // "in" | "out"
  onClose,
  onSuccess,            // (result) → parent refreshes & shows its own success
  onFallback,           // () → the regular punch
  strict = false,       // REQUIRED mode: the fallback only when the server allows it (retry_exhausted)
  onGoToProfile,        // () → enrollment page (409)
  isDarkMode,
  language,
}) {
  const t = faceText(language);
  const c = dialogColors(isDarkMode);
  const cam = useFaceCapture();

  const [phase, setPhase] = useState(PHASE.CAMERA);
  const [countdown, setCountdown] = useState(null);
  const [prompt, setPrompt] = useState(null);
  const [progress, setProgress] = useState(null);
  const [error, setError] = useState(null); // { title, message, code, server, hint, reenrol }
  const [rejections, setRejections] = useState(0);
  const runRef = useRef(0); // cancels an in-flight attempt when the window closes

  const resetAll = useCallback(() => {
    runRef.current += 1;
    setPhase(PHASE.CAMERA);
    setCountdown(null);
    setPrompt(null);
    setProgress(null);
    setError(null);
    setRejections(0);
  }, []);

  const attempt = useCallback(async () => {
    const run = ++runRef.current;
    const alive = () => run === runRef.current;
    setError(null);
    setProgress(0);
    setPhase(PHASE.COUNTDOWN);
    setPrompt(t.getReady);

    // The attempt's challenge: which way to turn, in which order, plus its nonce.
    let challenge = null;
    try {
      challenge = await faceApi.challenge();
    } catch (err) {
      if (!alive()) return;
      if (err?.status !== 404 && err?.status !== 501) {
        const d = describeFaceError(err, language);
        setPhase(PHASE.ERROR);
        setError({ ...d, reenrol: false });
        setProgress(null);
        return;
      }
      challenge = null; // older server: fixed order, no nonce
    }
    if (!alive()) return;
    const steps = (challenge?.steps?.length ? challenge.steps : DEFAULT_STEPS).filter((s) => t.prompts[s]);

    for (let n = COUNTDOWN_FROM; n >= 1; n -= 1) {
      if (!alive()) return;
      setCountdown(n);
      await sleep(1000);
    }
    if (!alive()) return;
    setCountdown(null);
    setPhase(PHASE.CAPTURING);

    // Prompts change with time, independent of frame timing, in the challenge's order.
    const prompts = steps.map((s) => t.prompts[s]);
    cam.setFakeMotion?.(steps, BURST_MS); // dev fake camera performs the same order
    const t0 = performance.now();
    const promptTimer = setInterval(() => {
      const i = Math.min(prompts.length - 1, Math.floor(((performance.now() - t0) / BURST_MS) * prompts.length));
      setPrompt(prompts[i]);
    }, 50);
    setPrompt(prompts[0]);

    let frames = null;
    try {
      frames = await cam.captureBurst(FRAME_COUNT, BURST_MS, (done, total) => { if (alive()) setProgress(done / total); });
    } catch (err) {
      clearInterval(promptTimer);
      if (!alive()) return;
      setPhase(PHASE.ERROR);
      setError({ title: t.failedTitle, message: t.cameraError });
      return;
    }
    clearInterval(promptTimer);
    if (!alive()) return;

    setPrompt(null);
    setPhase(PHASE.VERIFYING);
    try {
      const result = await faceApi.checkIn(frames, type, challenge?.nonce ? { challenge: challenge.challenge, nonce: challenge.nonce } : {});
      frames = null; // never kept
      if (!alive()) return;
      await onSuccess?.(result);
    } catch (err) {
      frames = null;
      if (!alive()) return;
      const d = describeFaceError(err, language);
      const reenrol = err?.status === 409 || err?.code === "reenrol_required";
      setRejections((r) => r + 1);
      setPhase(PHASE.ERROR);
      setError({
        ...d,
        hint: distanceHint(err, language),
        reenrol,
        exhausted: err?.code === "retry_exhausted",
        // The server says when a fallback punch would be accepted (two identity
        // rejections within the window); liveness/spoof rejections never allow one.
        allowFallback: err?.body?.allowFallback === true,
      });
      setProgress(null);
    }
  }, [cam, type, language, onSuccess, t]);

  // Open: start the camera, and run the first attempt once it is live.
  useEffect(() => {
    if (!open) { cam.stop(); resetAll(); return undefined; }
    let cancelled = false;
    (async () => {
      const ok = await cam.start();
      if (!cancelled && ok) { await sleep(500); if (!cancelled) attempt(); }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const busy = phase === PHASE.COUNTDOWN || phase === PHASE.CAPTURING || phase === PHASE.VERIFYING;
  // OPTIONAL: the regular punch is always a way out after two tries. REQUIRED
  // (strict): only when the server has said a fallback would be accepted —
  // offering it otherwise just produces fallback_not_allowed.
  const showFallback = Boolean(onFallback) && (strict ? Boolean(error?.allowFallback) : (rejections >= FALLBACK_AFTER || error?.exhausted));
  const cameraErrorLabel =
    cam.status === CAMERA_STATUS.DENIED ? t.cameraDenied
      : cam.status === CAMERA_STATUS.UNAVAILABLE ? t.cameraUnavailable
        : t.cameraError;
  const cameraFailed = cam.status === CAMERA_STATUS.DENIED || cam.status === CAMERA_STATUS.UNAVAILABLE || cam.status === CAMERA_STATUS.ERROR;

  const btn = (kind, disabled) => ({
    display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 8,
    flex: "1 1 170px", padding: "12px 18px", borderRadius: 12, fontSize: 15, fontWeight: 800, fontFamily: "inherit",
    border: kind === "ghost" ? `1px solid ${c.border}` : "none",
    background: kind === "primary" ? c.primary : kind === "warning" ? c.warning : "transparent",
    color: kind === "ghost" ? c.text : "#fff",
    cursor: disabled ? "not-allowed" : "pointer", opacity: disabled ? 0.55 : 1,
    boxShadow: kind === "ghost" ? "none" : `0 4px 14px ${kind === "warning" ? c.warning : c.primary}44`,
  });

  const fallbackLabel = type === "out" ? t.fallbackOut : t.fallbackIn;

  const footer = (
    <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
      {showFallback && (
        <>
          <div style={{ fontSize: 13, color: c.muted, fontWeight: 600, textAlign: "center" }}>{t.fallbackHint}</div>
          <button type="button" onClick={() => { onClose?.(); onFallback?.(); }} style={{ ...btn("warning", false), flex: "0 0 auto", width: "100%" }} data-testid="face-fallback">
            <KeyRound size={18} /> {fallbackLabel}
          </button>
        </>
      )}
      <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
        {error?.reenrol && onGoToProfile ? (
          <button type="button" onClick={() => { onClose?.(); onGoToProfile(); }} style={btn("primary", false)} data-testid="face-go-profile">
            <UserRound size={18} /> {t.goToProfile}
          </button>
        ) : (
          <button
            type="button"
            onClick={attempt}
            disabled={busy || !cam.isReady}
            style={btn(showFallback ? "ghost" : "primary", busy || !cam.isReady)}
            data-testid="face-retry"
          >
            <RotateCcw size={18} /> {phase === PHASE.ERROR ? t.retry : type === "out" ? t.checkOutTitle : t.checkInTitle}
          </button>
        )}
        <button type="button" onClick={onClose} disabled={phase === PHASE.VERIFYING} style={btn("ghost", phase === PHASE.VERIFYING)}>
          {t.cancel}
        </button>
      </div>
      {cameraFailed && onFallback && !showFallback && !strict && (
        <button type="button" onClick={() => { onClose?.(); onFallback?.(); }} style={{ ...btn("ghost", false), flex: "0 0 auto", width: "100%" }} data-testid="face-fallback-camera">
          <KeyRound size={16} /> {fallbackLabel}
        </button>
      )}
      {strict && !showFallback && (phase === PHASE.ERROR || cameraFailed) && (
        <div style={{ fontSize: 12, color: c.muted, textAlign: "center" }} data-testid="face-required-note">{t.requiredNoFallback}</div>
      )}
    </div>
  );

  return (
    <RemotePunchDialog
      open={open}
      onClose={phase === PHASE.VERIFYING ? undefined : onClose}
      title={type === "out" ? t.checkOutTitle : t.checkInTitle}
      subtitle={phase === PHASE.ERROR ? t.attemptsLeftHint : t.getReady}
      icon={ScanFace}
      iconColor={phase === PHASE.ERROR ? c.danger : c.primary}
      dark={isDarkMode}
      language={language}
      maxWidth={520}
      closeLabel={t.close}
      footer={footer}
    >
      <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
        <FaceCameraView
          videoRef={cam.videoRef}
          previewStyle={cam.previewStyle}
          status={cam.status}
          prompt={prompt}
          countdown={countdown}
          progress={phase === PHASE.CAPTURING || phase === PHASE.VERIFYING ? progress : null}
          busyLabel={phase === PHASE.VERIFYING ? t.verifying : null}
          errorLabel={cameraErrorLabel}
          onRetryCamera={cam.start}
          retryLabel={t.retry}
          dark={isDarkMode}
          guideColor={phase === PHASE.ERROR ? c.danger : phase === PHASE.CAPTURING ? c.success : c.primary}
        />

        {error && (
          <div role="alert" data-testid="face-error" style={{ padding: "12px 14px", borderRadius: 12, background: `${c.danger}14`, border: `1px solid ${c.danger}55`, color: c.text, fontSize: 13.5, lineHeight: 1.7 }}>
            <div style={{ fontWeight: 800, color: c.danger }}>{error.title}</div>
            <div>{error.message}</div>
            {error.server && <div style={{ color: c.muted, fontSize: 12.5 }}>{error.server}</div>}
            {error.hint && (
              <div style={{ marginTop: 6, display: "inline-block", padding: "4px 12px", borderRadius: 999, background: `${c.warning}22`, color: c.warning, fontWeight: 800, fontSize: 13 }}>
                {error.hint}
              </div>
            )}
          </div>
        )}
      </div>
    </RemotePunchDialog>
  );
}
